<script setup lang="ts">
import { PropType } from 'vue';
import { RouterLink } from 'vue-router';
import type { NavItem } from './types';

const props = defineProps({
  item: { type: Object as PropType<NavItem>, required: true },
  align: { type: String as PropType<'start' | 'end'>, default: 'start' },
});

const emit = defineEmits<{
  (event: 'navigate', item: NavItem): void;
  (event: 'action', item: NavItem): void;
}>();

const hasChildren = (item: NavItem) =>
  Array.isArray(item.children) && item.children.length > 0;
</script>

<template>
  <li v-if="!hasChildren(item)" class="nav-item">
    <RouterLink
      v-if="item.to && !item.disabled"
      class="nav-link"
      :to="item.to"
      @click="emit('navigate', item)"
    >
      <slot name="icon" :item="item" /> {{ item.label }}
    </RouterLink>

    <button
      v-else-if="!item.to && !item.disabled"
      class="nav-link"
      type="button"
      @click="emit('action', item)"
    >
      <slot name="icon" :item="item" /> {{ item.label }}
    </button>

    <span v-else class="nav-link disabled" aria-disabled="true">
      <slot name="icon" :item="item" /> {{ item.label }}
    </span>
  </li>

  <li
    v-else
    class="nav-item dropdown"
    :class="align === 'end' ? 'dropstart-lg' : ''"
  >
    <a
      href="#"
      class="nav-link dropdown-toggle"
      data-bs-toggle="dropdown"
      role="button"
      aria-expanded="false"
    >
      <slot name="icon" :item="item" /> {{ item.label }}
    </a>

    <ul
      class="dropdown-menu"
      :class="align === 'end' ? 'dropdown-menu-end' : ''"
    >
      <template v-for="child in item.children" :key="child.id || child.label">
        <li v-if="hasChildren(child)" class="dropdown-submenu">
          <a href="#" class="dropdown-item dropdown-toggle">{{
            child.label
          }}</a>
          <ul class="dropdown-menu">
            <li v-for="sub in child.children!" :key="sub.id || sub.label">
              <RouterLink
                v-if="sub.to && !sub.disabled"
                class="dropdown-item"
                :to="sub.to"
                @click="emit('navigate', sub)"
                >{{ sub.label }}</RouterLink
              >
              <span
                v-else
                class="dropdown-item disabled"
                aria-disabled="true"
                >{{ sub.label }}</span
              >
            </li>
          </ul>
        </li>

        <li v-else>
          <RouterLink
            v-if="child.to && !child.disabled"
            class="dropdown-item"
            :to="child.to"
            @click="emit('navigate', child)"
            >{{ child.label }}</RouterLink
          >

          <button
            v-else-if="!child.to && !child.disabled"
            class="dropdown-item"
            type="button"
            @click="emit('action', child)"
          >
            {{ child.label }}
          </button>

          <span v-else class="dropdown-item disabled" aria-disabled="true">{{
            child.label
          }}</span>
        </li>
      </template>
    </ul>
  </li>
</template>

<style scoped>
.dropdown-submenu {
  position: relative;
}

@media (min-width: 992px) {
  .dropdown-submenu > .dropdown-menu {
    top: 0;
    left: 100%;
    margin-left: 0.25rem;
    margin-top: -0.25rem;
  }

  .dropdown-submenu > .dropdown-item.dropdown-toggle::after {
    content: '›';
    float: right;
    opacity: 0.6;
  }
}

@media (max-width: 991.98px) {
  .dropdown-submenu > .dropdown-menu {
    left: 0;
    top: auto;
    margin-left: 0;
  }
}
</style>
